import { useGetAiWorkersByCategoryQuery, useTypedSelector, categoryType, useDeleteAiWorkersMutation, RouteState, useEntityResourcePermissions } from '@circuitry-ai/doc-data';
import {
  CardListViewMode,
  EntityCardWithActions,
  EntityListView,
  ListActionsCellWithDelete,
  checkType,
  CtyButton,
  CtyShowToast,
  getActionsColumnDef,
  getDescriptionColumnDef,
  getNameColumnDef,
} from '@circuitry-ai/doc-ui';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AnalystTabs } from './analystBoard';

function buildReportUrl(report: any, tabValue?: string): string {
  const reportID = report.aiworkers_attrs?.find((attr: any) => attr.attr_name === 'report_id')?.attr_value;
  const analystType = report.aiworkers_attrs?.find((attr: any) => attr.attr_name === 'analyst_type')?.attr_value;
  const reportType = report.aiworkers_attrs?.find((attr: any) => attr.attr_name === 'report_type')?.attr_value;
  const aiworkerId = report.aiworker_id;
  const tab = tabValue ?? (analystType === 'prescriptive' ? AnalystTabs.CLAIM_SCORE : AnalystTabs.DASHBOARD);
  const params = new URLSearchParams({ tab, aiworkerId });
  if (reportID) params.append('reportId', reportID);
  if (analystType === 'evaluations' || analystType === 'decision' || analystType === 'pyanalyst') {
    params.append('analystType', analystType);
    if (analystType === 'evaluations' && reportType) params.append('reportType', reportType);
  }
  return `/analytics/${RouteState.VIEW}?${params.toString()}`;
}

function useDeleteAnalystHandler(report: any, refetchAnalyst: () => void) {
  const [deleteAiWorkers, { isLoading: deleteAnalystLoading }] = useDeleteAiWorkersMutation();

  const handleConfirmDelete = useCallback(async (closeModal: () => void) => {
    try {
      const response = await deleteAiWorkers(report?.aiworker_id);
      CtyShowToast(checkType.success, response?.data);
      closeModal();
      refetchAnalyst();
    } catch (error) {
      CtyShowToast(checkType.error, (error as Error)?.message || 'Delete Operation failed');
    }
  }, [deleteAiWorkers, report?.aiworker_id, refetchAnalyst]);

  return { handleConfirmDelete, isDeleteLoading: deleteAnalystLoading };
}

const AnalystCardItem = ({ report, refetchAnalyst }: { readonly report: any; readonly refetchAnalyst: () => void }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canRead, canUpdate, canDelete } = useEntityResourcePermissions('analyst', report?.aiworker_id);
  const { handleConfirmDelete, isDeleteLoading } = useDeleteAnalystHandler(report, refetchAnalyst);

  const footerItems = (
    <>
      <CtyButton className={'w-full border-1'} variant={'bordered'} radius='sm' size={'lg'} color={'default'} isDisabled={!canUpdate} onPress={() => navigate(buildReportUrl(report, AnalystTabs.CONFIGURATION), { state: { report } })}>
        {t('Edit')}
      </CtyButton>
      <CtyButton className={'w-full'} variant={'solid'} radius='sm' size={'lg'} color={'primary'} isDisabled={!canRead} onPress={() => navigate(buildReportUrl(report), { state: { report } })}>
        {t('Launch')}
      </CtyButton>
    </>
  );

  return (
    <EntityCardWithActions
      id={report.aiworker_id}
      name={report.name}
      description={report.description}
      view={report.category === categoryType.ANALYST}
      footerItems={footerItems}
      canDelete={canDelete}
      dropdownAriaLabel="Analyst Actions"
      deleteModalEntityName={report?.name ?? ''}
      onConfirmDelete={handleConfirmDelete}
      isDeleteLoading={isDeleteLoading}
      deleteConfirmLabel={t('Delete Analyst')}
    />
  );
};

const AnalystActionsCell = ({ report, refetchAnalyst }: { readonly report: any; readonly refetchAnalyst: () => void }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canRead, canUpdate, canDelete } = useEntityResourcePermissions('analyst', report?.aiworker_id);
  const { handleConfirmDelete, isDeleteLoading } = useDeleteAnalystHandler(report, refetchAnalyst);

  return (
    <ListActionsCellWithDelete
      actions={[
        {
          label: t('Edit'),
          onPress: () => navigate(buildReportUrl(report, AnalystTabs.CONFIGURATION), { state: { report } }),
          variant: 'bordered',
          color: 'default',
          isDisabled: !canUpdate,
        },
        {
          label: t('Launch'),
          onPress: () => navigate(buildReportUrl(report), { state: { report } }),
          variant: 'solid',
          color: 'primary',
          isDisabled: !canRead,
        },
      ]}
      canDelete={canDelete}
      entityName={report?.name ?? ''}
      onConfirmDelete={handleConfirmDelete}
      isDeleteLoading={isDeleteLoading}
      deleteConfirmLabel={t('Delete Analyst')}
    />
  );
};

const renderAnalystActionsCell = (refetchAnalyst: () => void) =>
  function AnalystActionsRenderer(params: any) {
    return <AnalystActionsCell report={params?.data} refetchAnalyst={refetchAnalyst} />;
  };

export function AnalyticsList() {
  const token = useTypedSelector((state) => state.auth.token);
  const [skip, setSkip] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [viewMode, setViewMode] = useState<CardListViewMode>(CardListViewMode.GRID);
  const { data: reports, error, isLoading, isFetching, refetch } = useGetAiWorkersByCategoryQuery({ category: categoryType.ANALYST }, { skip, refetchOnMountOrArgChange: true });
  const { t } = useTranslation();
  useEffect(() => {
    if (token && skip) {
      setSkip(false);
    }
  }, [skip, token]);

  const analystReports = useMemo(
    () => (reports ?? []).filter((report: any) => report.category === 'analyst'),
    [reports]
  );

  const filteredReports = useMemo(() => {
    if (!searchQuery.trim()) return analystReports;
    return analystReports.filter((report: any) =>
      report.name?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [analystReports, searchQuery]);

  const columnDefs = useMemo(
    () => [
      getNameColumnDef(t('Name')),
      getDescriptionColumnDef(t('Description')),
      getActionsColumnDef({
        headerName: t('Actions'),
        minWidth: 240,
        maxWidth: 280,
        cellRenderer: renderAnalystActionsCell(refetch),
      }),
    ],
    [t, refetch]
  );

  if (analystReports.length === 0 && !isLoading && !isFetching && !error) {
    return (
      <div className="flex-1 flex items-center justify-center h-full border-2 border-default-300 rounded-lg m-4 bg-default-100">
        <div className="text-2xl text-primary">
          {t('No Analysts found')}
        </div>
      </div>
    );
  }

  return (
    <EntityListView
      items={filteredReports}
      isLoading={isLoading || isFetching}
      error={error}
      searchId="analysts-search"
      searchPlaceholder={t('Search analysts')}
      onSearch={setSearchQuery}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      emptyMessage={t('No Analysts found')}
      cardKey={(report: any) => report.aiworker_id}
      renderCard={(report: any) => (
        <AnalystCardItem report={report} refetchAnalyst={refetch} />
      )}
      tableId="analysts-list-table"
      columnDefs={columnDefs}
    />
  );
}

export default AnalyticsList;
